import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, columnTypes, styles, percent, ratio, number, pitchTypeDisplayName } from 'yankees-react-utils';
import styled from 'styled-components';
import { kzoneAccuracy, pitchFreq } from './utils';
import ReportCard from '../../../../components/ReportCard';

const StyledReportCard = styled(ReportCard)`
    .table {
        text-align: center;
    }
    th, td {
        border-right: 1px solid ${styles.theme.border.bombay} !important;
        border-bottom: 1px solid ${styles.theme.border.bombay} !important;
    }
    .highlight {
        background-color: #ffff77 !important;
    }
    .pitch-type {
        background-color: ${styles.theme.background.geyser} !important;
        box-shadow: none !important; 
        font-weight: bold;
    }
    .high-usage, .low-usage {
        font-weight: bold !important;
    }
    .high-usage {
        background-color: #AACCFF !important;
    }
    .low-usage {
        background-color: #F2B9B4 !important;
    }
    .kzon-row {
        border-top: 2px solid ${styles.theme.background.boulder} !important;
    }
    .kzon-column {
        border-right: 2px solid ${styles.theme.background.boulder} !important;
    }
`;

const PitchFrequencyTable = ({ className, isVsBatter, expected, kzon, lgavgkzon, numFbInRepertoire }) => {
    const { t: translate } = useTranslation();

    if (!expected?.length) {
        return <div className="d-flex justify-content-center py-5 text-muted">{translate('sections.mechanics-workload.no-data')}</div>;
    }
    const columns = [{
        id: 'title',
        title: translate('data.pitch-type'),
        type: columnTypes.TEXT,
        cellClass: (v) => `pitch-type ${v.highlight ? 'highlight' : ''} ${v.titleCellClass}`,
    }, {
        id: 'pit',
        title: translate('pitcher-overview.pitch-break.pit-percent'),
        type: columnTypes.TEXT,
        cellClass: (v) => v.pitCellClass,
    }, {
        id: 'twoStrikes',
        title: translate('pitcher-overview.pitch-frequency.two-strike-perc'),
        type: columnTypes.TEXT,
        cellClass: (v) => `${v.twoStrikesCellClass} kzon-column`,
        headerClass: 'kzon-column',
    }, ...['00', '01', '02', '10', '11', '12', '20', '21', '22', '30', '31', '32'].map((count) => ({
        id: count,
        title: count.split('').join('-'),
        type: columnTypes.TEXT,
        cellClass: (v) => v[`${count}CellClass`],
    }))];

    const individualPitches = expected.map((row) => {
        const newRow = {
            title: pitchTypeDisplayName(row, true, true, translate),
            highlight: row.highlight,
            pit: percent(ratio(row.pitches, row.totalpitches), 0),
            pitCellClass: pitchFreq(row, 'pit', 'totalpitches', 'pitches', 'totalpitches', numFbInRepertoire, lgavgkzon),
            twoStrikes: percent(ratio(row.p2k, row.t2k), 0),
            twoStrikesCellClass: pitchFreq(row, '2k', 'totalpitches', null, null, numFbInRepertoire, lgavgkzon),
        };
        ['00', '01', '02', '10', '11', '12', '20', '21', '22', '30', '31', '32'].forEach((count) => {
            newRow[count] = percent(ratio(row[`p${count}`], row[`t${count}`]), 0);
            newRow[`${count}CellClass`] = pitchFreq(row, count, 'totalpitches', null, null, numFbInRepertoire, lgavgkzon);
        });

        return newRow;
    });
    const totalPitchRow = {
        title: translate('baseball.pitches_other'),
        pit: number(expected[0].totalpitches),
        pitCellClass: 'fw-bold',
        twoStrikes: number(expected[0].t2k),
        twoStrikesCellClass: 'fw-bold',
    };
    const kZoneRow = {
        title: translate('sections.reports.pitcher.kzon-pct'),
        titleCellClass: 'kzon-row',
        pit: percent(ratio(kzon[0].kzonpit, kzon[0].tpit), 0),
        pitCellClass: `${kzoneAccuracy(kzon[0].kzonpit, kzon[0].tpit, 'tpit', null, lgavgkzon)} kzon-row`,
        twoStrikes: percent(ratio(kzon[0].kzon2k, kzon[0].t2k), 0),
        twoStrikesCellClass: `${kzoneAccuracy(kzon[0].kzon2k, kzon[0].t2k, 't2k', null, lgavgkzon)} kzon-row`,
    };
    const leagueAvgKZoneRow = {
        title: translate('sections.reports.pitcher.mlb-avg-kzon-pct'),
        pit: percent(ratio(lgavgkzon[0].kzonpit, lgavgkzon[0].tpit), 0),
        twoStrikes: percent(ratio(lgavgkzon[0].kzon2k, lgavgkzon[0].t2k), 0),
    };
    ['00', '01', '02', '10', '11', '12', '20', '21', '22', '30', '31', '32'].forEach((t) => {
        totalPitchRow[t] = number(expected[0][`t${t}`]);
        totalPitchRow[`${t}CellClass`] = 'fw-bold';

        kZoneRow[t] = percent(ratio(kzon[0][`kzon${t}`], kzon[0][`t${t}`]), 0);
        kZoneRow[`${t}CellClass`] = `${kzoneAccuracy(kzon[0][`kzon${t}`], kzon[0][`t${t}`], t, null, lgavgkzon)} kzon-row`;

        leagueAvgKZoneRow[t] = percent(ratio(lgavgkzon[0][`kzon${t}`], lgavgkzon[0][`t${t}`]), 0);
    });

    const rows = [...individualPitches, totalPitchRow, kZoneRow, leagueAvgKZoneRow];

    return (
        <StyledReportCard
            className={className}
            title={`${translate('pitcher-overview.sections.pitch-frequency')} ${isVsBatter ? `(${translate('common.projected')})` : ''}`}
        >
            <DataTable
                superheader={(
                    <tr>
                        <th className="table-title" colSpan={columns.length}>
                            {translate('pitcher-overview.sections.pitch-frequency')}
                        </th>
                    </tr>
                )}
                columns={columns}
                rows={rows}
            />
        </StyledReportCard>
    );
};

export default PitchFrequencyTable;
